import {
    CodedProperty,
    createFailure,
    createValidationResult,
    depascalize,
    FailureSeverity,
    NamedProperty,
    ValidationFailure,
    ValidationResult
} from "sprint-common-web";

import { FileRecordStatus } from "../../../../common/types/FileRecordStatus";
import { RecordType } from "../../../../common/types/RecordType";

export type CanFileRecordBeChangedToQcDoneArgs = {
    recordType: RecordType;
    versions: string[];
    hasSubtitle?: boolean;
    soundtracks?: CodedProperty[];
    status: FileRecordStatus;
    metadata?: { aspectRatio?: NamedProperty; duration?: string };
};

export const validateFileRecordStatus = (
    item: CanFileRecordBeChangedToQcDoneArgs,
    selectedStatus: FileRecordStatus
): boolean => {
    switch (selectedStatus) {
        case FileRecordStatus.Editing:
            return canFileRecordBeChangedToEditing(item.status);
        case FileRecordStatus.Destroyed:
            return canFileRecordBeChangedToDestroyed(item.status);
        case FileRecordStatus.QcDone:
            return canFileRecordBeChangedToQcDone(item);
        case FileRecordStatus.Qc:
            return canFileRecordBeChangedToQc(item.status);
        case FileRecordStatus.Rejected:
            return canFileRecordBeChangedToRejected(item.status);
        default:
            return false;
    }
};

export const canFileRecordBeChangedToRejected = (status: FileRecordStatus): boolean =>
    [
        FileRecordStatus.Editing,
        FileRecordStatus.Qc,
        FileRecordStatus.Created,
        FileRecordStatus.WaitingForClientResponse
    ].includes(status);

export const canFileRecordBeChangedToDestroyed = (status: FileRecordStatus): boolean =>
    status === FileRecordStatus.QcDone || status === FileRecordStatus.Rejected;

export const canFileRecordBeChangedToEditing = (status: FileRecordStatus): boolean =>
    status !== FileRecordStatus.Destroyed &&
    status !== FileRecordStatus.Rejected &&
    status !== FileRecordStatus.QcDone &&
    status !== FileRecordStatus.Editing;

export const canFileRecordBeChangedToQc = (status: FileRecordStatus): boolean =>
    [FileRecordStatus.Created, FileRecordStatus.Editing, FileRecordStatus.WaitingForClientResponse].includes(status);

export const canFileRecordBeChangedToQcDone = (args: CanFileRecordBeChangedToQcDoneArgs): boolean => {
    return validateIfFileRecordCanBeChangedToQcDone(args).isValid;
};

export const canFileRecordBeChangedToWaitingForClientResponse = (status: FileRecordStatus): boolean => {
    return [FileRecordStatus.Created, FileRecordStatus.Qc, FileRecordStatus.Editing].includes(status);
};

export const validateIfFileRecordCanBeChangedToQcDone = (
    args: CanFileRecordBeChangedToQcDoneArgs
): ValidationResult => {
    const hasContentVersionIds = args.versions.length > 0;

    const failures = [];

    switch (args.recordType) {
        case RecordType.Video: {
            if (!hasContentVersionIds) {
                failures.push(missingContentVersionFailure());
            }

            if (!hasValidSoundtracks(args.soundtracks)) {
                failures.push(missingSoundtrackFailure());
            }

            if (!args.hasSubtitle) {
                failures.push(missingSubtitleFailure());
            }

            if (!args.metadata?.duration) {
                failures.push(missingVideoDurationFailure());
            }

            if (!hasValidAspectRatio(args.metadata?.aspectRatio)) {
                failures.push({
                    code: "missing.aspectRatio",
                    message: `Missing aspect ratio`,
                    severity: FailureSeverity.Error
                });
            }
            break;
        }
        case RecordType.Audio: {
            if (!hasContentVersionIds) {
                failures.push(missingContentVersionFailure());
            }

            if (!hasValidSoundtracks(args.soundtracks)) {
                failures.push(missingSoundtrackFailure());
            }
            break;
        }
        case RecordType.Subtitle: {
            if (!hasContentVersionIds) {
                failures.push(missingContentVersionFailure());
            }

            if (!args.hasSubtitle) {
                failures.push(missingSubtitleFailure());
            }
            break;
        }
        default:
            return createValidationResult([createFailure("unsupported.RecordType", "Unsupported file record type")]);
    }

    if (
        args.status === FileRecordStatus.Destroyed ||
        args.status === FileRecordStatus.Rejected ||
        args.status === FileRecordStatus.QcDone
    ) {
        failures.push(wrongStatusFailure(args.status));
    }

    return createValidationResult(failures);
};

const missingSoundtrackFailure = (): ValidationFailure => ({
    code: "missing.soundtracks",
    message: `Missing soundtrack`,
    severity: FailureSeverity.Error
});

const missingSubtitleFailure = (): ValidationFailure => ({
    code: "missing.subtitle",
    message: `Missing subtitle`,
    severity: FailureSeverity.Error
});

const wrongStatusFailure = (status: FileRecordStatus): ValidationFailure => ({
    code: "missing.wrongStatus",
    message: `Cannot change status from ${depascalize(FileRecordStatus[status])}`,
    severity: FailureSeverity.Error
});

const missingContentVersionFailure = (): ValidationFailure => ({
    code: "missing.contentVersion",
    message: `Missing content version`,
    severity: FailureSeverity.Error
});

const missingVideoDurationFailure = (): ValidationFailure => ({
    code: "missing.videoDuration",
    message: `Missing duration`,
    severity: FailureSeverity.Error
});

const hasValidSoundtracks = (soundtracks: CodedProperty[] | undefined): boolean =>
    soundtracks !== undefined && soundtracks.length > 0;

const hasValidAspectRatio = (aspectRatio: NamedProperty | undefined): boolean => aspectRatio !== undefined;
